/**
 * The store's granted Shopify scopes, as `/stores/detail` reports them
 * (AS-11280).
 *
 * The bot caches the scopes and, when Shopify cannot be asked and nothing is
 * cached, answers `shopifyScopesKnown: false` with an empty list. An empty list
 * used to mean "scope missing" here and put the re-grant prompt in front of
 * merchants who have the scope, so every check below treats unknown scopes as
 * granted. An older bot never sends the flag: absent means known.
 *
 * Import-light on purpose: mocha cannot load `models/store` (it reaches main.ts).
 */
export function parseShopifyScopesKnown(value: unknown): boolean {
  return value !== false;
}

function includesScope(scopes: string | null | undefined, scope: string): boolean {
  return Boolean(scopes && scopes.includes(scope));
}

/** `StoreModel.needsInventoryScope()`: the PO-detail re-grant trigger. */
export function needsInventoryScope(
  scopes: string | null | undefined,
  scopesKnown: boolean,
  operationalModeUnlocked: boolean,
): boolean {
  if (!scopesKnown) return false;
  if (includesScope(scopes, 'write_inventory')) return false;
  return operationalModeUnlocked;
}

/** `StoreModel.needsInventoryTransferScope()`: the transfer permission dialog. */
export function needsInventoryTransferScope(scopes: string | null | undefined, scopesKnown: boolean): boolean {
  if (!scopesKnown) return false;
  return !includesScope(scopes, 'write_inventory_transfers');
}

/**
 * `StoreModel.hasInventoryTransferPermission()`. Unknown reads as granted, to
 * match the skipped dialog: the transfer goes to Shopify, which refuses it
 * with an error if the scope really is missing.
 */
export function hasInventoryTransferPermission(scopes: string | null | undefined, scopesKnown: boolean): boolean {
  if (!scopesKnown) return true;
  return includesScope(scopes, 'write_inventory_transfers');
}
